<div class="contact-overlay" [class.open]="isOpen" (click)="onClose()">
  <div class="contact-modal" #contactDialog role="dialog" [attr.aria-modal]="isOpen ? 'true' : null" aria-labelledby="contact-dialog-title" [attr.dir]="getDirection()" (click)="$event.stopPropagation()" (keydown)="onDialogKeydown($event)">

    <!--
      a11y: header Tab-order is "close button → h2 title → rest of dialog".
      Audit (contact dialog): "יש להקפיץ את הפוקוס לכפתור סגירה המסומן בחץ
      אדום, ומשם לכותרת צור קשר ומשם להמשך העמוד".
      - close button comes FIRST in the DOM so Tab after the initial focus
        lands on the h2 next;
      - h2 carries tabindex="0" so it actually receives keyboard focus;
      - visual layout (title on the start side, X on the end side) is
        preserved via flex `order` in the SCSS, decoupled from DOM order.
    -->
    <div class="contact-header">
      <button class="close-button" #contactCloseBtn type="button" [attr.tabindex]="isOpen ? null : -1" (click)="onClose()" [attr.aria-label]="closeAriaLabel">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h2 id="contact-dialog-title" class="contact-title" tabindex="0">{{ modalTitle }}</h2>
    </div>

    <!-- Content -->
    <div class="contact-content">

      <!-- Phone & Service Hours -->
      <div class="contact-section">
        <div class="section-header">
          <div class="section-icon phone-icon">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="20" fill="#E7EAEE"/>
              <path d="M12 13L16.5 12L19 16.5L16.375 18.5938C17.5 20.75 19.25 22.5 21.4062 23.625L23.5 21L28 23.5L27 28H26C18.25 28 12 21.75 12 14V13Z" fill="#0C3058"/>
            </svg>
          </div>
          <div class="section-content">
            <h3 class="section-title">{{ contactInfo?.phoneTitle }}</h3>

            <div class="phone-numbers">
              @for (phone of contactInfo?.phoneNumbers; track phone; let i = $index) {
                <a
                  [href]="'tel:' + phone"
                  class="phone-link">
                  {{ phone }}
                </a>
              }
            </div>

            <div class="service-hours">
              <p class="hours-text">{{ contactInfo?.serviceHours?.weekdays || '' }}</p>
              <p class="hours-text">{{ contactInfo?.serviceHours?.weekend || '' }}</p>
              <p class="hours-text">{{ contactInfo?.serviceHours?.saturdayNight || '' }}</p>
            </div>
          </div>
        </div>

        <!-- Address -->
        <div class="contact-section">
          <div class="section-header">
            <div class="section-icon location-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="20" fill="#E7EAEE"/>
                <path d="M20 28C20 28 14 21.5 14 18C14 14.6875 16.6875 12 20 12C23.3125 12 26 14.6875 26 18C26 21.5 20 28 20 28ZM20 16C19.2812 16 18.625 16.4062 18.25 17C17.9062 17.625 17.9062 18.4062 18.25 19C18.625 19.625 19.2812 20 20 20C20.6875 20 21.3438 19.625 21.7188 19C22.0625 18.4062 22.0625 17.625 21.7188 17C21.3438 16.4062 20.6875 16 20 16Z" fill="#0C3058"/>
              </svg>
            </div>
            <div class="section-content">
              <h3 class="section-title">{{ contactInfo?.addressTitle }}</h3>
              <p class="address-text">{{ contactInfo?.address || '' }}</p>
            </div>
          </div>
        </div>

        <!-- Email -->
        <div class="contact-section">
          <div class="section-header">
            <div class="section-icon email-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="20" fill="#E7EAEE"/>
                <path d="M12 14H28V16.5L20 22L12 16.5V14ZM12 26V17.7188L19.4062 22.8438L20 23.2188L20.5625 22.8438L28 17.7188V26H12Z" fill="#0C3058"/>
              </svg>
            </div>
            <div class="section-content">
              <h3 class="section-title">{{ contactInfo?.emailTitle }}</h3>
              @if (contactInfo?.email) {
                <a
                  [href]="'mailto:' + contactInfo?.email"
                class="email-link">{{ contactInfo?.email }}</a>
              }
            </div>
          </div>
        </div>

        <!-- Languages -->
        <div class="contact-section">
          <div class="section-header">
            <div class="section-icon globe-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="20" fill="#E7EAEE"/>
                <path d="M23 20C23 20.7188 22.9062 21.375 22.7812 22H17.1875C17.0625 21.375 17 20.7188 17 20C17 19.3125 17.0625 18.6562 17.1875 18H22.7812C22.9062 18.6562 23 19.3125 23 20ZM23.8125 18H27.7188C27.9062 18.6562 28 19.3125 28 20C28 20.7188 27.9062 21.375 27.7188 22H23.8125C23.9375 21.375 24 20.7188 24 20C24 19.3125 23.9375 18.6562 23.8125 18ZM27.4062 17H23.5938C23.0625 14.9688 22.0938 13.25 21.2812 12.125C24.0625 12.5625 26.375 14.4688 27.4062 17ZM22.5625 17H17.4062C17.7812 15.75 18.3125 14.6562 18.8438 13.7188C19.25 13.0312 19.6562 12.4688 20 12.0625C20.3125 12.4688 20.7188 13.0312 21.125 13.7188C21.6562 14.625 22.2188 15.75 22.5625 17ZM16.375 17H12.5625C13.5938 14.4688 15.9062 12.5625 18.6875 12.125C17.875 13.25 16.9062 14.9688 16.375 17ZM12.25 18H16.1562C16.0625 18.6562 16 19.3125 16 20C16 20.7188 16.0625 21.375 16.1562 22H12.25C12.0625 21.375 12 20.7188 12 20C12 19.3125 12.0625 18.6562 12.25 18ZM17.4062 23H22.5625C22.1875 24.2812 21.6562 25.375 21.125 26.3125C20.7188 27 20.3125 27.5625 20 27.9688C19.6562 27.5625 19.25 27 18.8438 26.3125C18.3125 25.375 17.7812 24.2812 17.4062 23ZM16.375 23C16.9062 25.0625 17.875 26.75 18.6875 27.9062C15.9062 27.4688 13.5938 25.5625 12.5625 23H16.375ZM27.4062 23C26.375 25.5625 24.0625 27.4688 21.2812 27.9062C22.0938 26.75 23.0625 25.0625 23.5938 23H27.4062Z" fill="#0C3058"/>
              </svg>
            </div>
            <div class="section-content">
              <h3 class="section-title">{{ contactInfo?.serviceLanguagesTitle }}</h3>
              <p class="languages-text">{{ contactInfo?.languages?.join(', ') || '' }}</p>
            </div>
          </div>
        </div>

        <!-- Additional Links -->
        <div class="contact-section">
          <div class="section-header">
            <div class="section-icon info-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="20" fill="#E7EAEE"/>
                <path d="M21 13V15.5H18.5V13H21ZM17 18H18H20H21V19V26H22H23V28H22H21H19H18H17V26H18H19V20H18H17V18Z" fill="#0C3058"/>
              </svg>
            </div>
            <div class="section-content">
              <a [href]="publicReceptionUrl" target="_blank" rel="noopener noreferrer" class="link-text">{{ contactInfo?.publicReceptionLink || '' }}</a>
            </div>
          </div>
        </div>

        <div class="contact-section">
          <div class="section-header">
            <div class="section-icon external-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="20" fill="#E7EAEE"/>
                <path d="M24 15H25V16V23V24H23V23V18.4375L16.6875 24.7188L16 25.4375L14.5625 24L15.2812 23.3125L21.5625 17H17H16V15H17H24Z" fill="#0C3058"/>
              </svg>
            </div>
            <div class="section-content">
              <a [href]="complaintUrl" target="_blank" rel="noopener noreferrer" class="link-text">{{ contactInfo?.accessibilityCommitteeLink || '' }}</a>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="contact-actions">
        <button class="action-button whatsapp-button" (click)="openWhatsApp()">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" fill="white"/>
          </svg>
          {{ viaWhatsAppLabel }}
        </button>

        <button class="action-button telegram-button" (click)="openTelegram()">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" fill="white"/>
          </svg>
          {{ viaTelegramLabel }}
        </button>
      </div>
    </div>
  </div>
